import { useRef } from "react";
import { t } from "../../i18n";
import { Toggle } from "../Toggle";
import { normalizeSchema } from "../tools/ToolConfigFields";

/**
 * Renders a JSON-schema config form. `saved` tells the form that the stored
 * configuration already contains values (including secrets) that are
 * preserved when a field is left blank — the provider editor passes
 * `provider.instanceConfigConfigured` here.
 *
 * Secret values are never returned to the browser, so an untouched secret
 * field renders the schema default (or `false` for booleans) plus a
 * "Saved · Clear saved" affordance when `saved` is true. The chosen behavior:
 *  - blank/untouched secret  -> server keeps the saved value;
 *  - typed/toggled value     -> server stores the new value;
 *  - "Clear saved"           -> the field is set to `null`, and the API's
 *    PATCH merge treats `null` as "remove the saved value entirely".
 *
 * "Clear saved" is only offered for optional fields: clearing a field listed
 * in `schema.required` would leave the config invalid by definition and the
 * API rejects it with a 400, so the button is hidden for required fields
 * (the "Saved" marker stays).
 */
export function ConfigFields({
  schema: rawSchema,
  value,
  onChange,
  disabled,
  saved = false,
}: {
  schema: Record<string, unknown>;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  disabled?: boolean;
  saved?: boolean;
}) {
  const schema = normalizeSchema(rawSchema);
  // Names of secret fields the user has cleared ("Clear saved" clicked) in
  // this editing session. Once a field is cleared, a blank stays a blank:
  // typing a value and then deleting it keeps the clear intent instead of
  // silently restoring the saved value on save (see `set` below).
  const clearedRef = useRef<Set<string>>(new Set());
  const propertiesKey = Object.keys(schema.properties ?? {}).join(",");
  const previousPropertiesKey = useRef(propertiesKey);
  if (previousPropertiesKey.current !== propertiesKey) {
    // Switching definitions resets which fields were cleared.
    previousPropertiesKey.current = propertiesKey;
    clearedRef.current = new Set();
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {Object.entries(schema.properties ?? {}).map(([name, property]) => {
        const label = property.title || name;
        const isSecret = property.secret === true;
        // `undefined` means the user has not touched this field in the current
        // session (secret values are never pre-filled); `null` is the armed
        // "clear saved secret" sentinel.
        const untouched = value[name] === undefined;
        const savedSecret = isSecret && saved && untouched;
        // While the clear sentinel is armed the field renders empty — never
        // the schema default — so a cleared secret cannot masquerade as a
        // value (and a cleared boolean renders unchecked).
        const cleared = value[name] === null;
        const current = cleared
          ? property.type === "boolean"
            ? false
            : ""
          : (value[name] ??
            property.default ??
            (property.type === "boolean" ? false : ""));
        const set = (next: unknown) => {
          if (next === null) clearedRef.current.add(name);
          // A secret field's blank only means "keep the saved value" while
          // the field is untouched. Once the user cleared it, any subsequent
          // blank (typed then deleted) stays a clear (`null`) until a real
          // value is typed, so blank-after-clear still means "clear".
          const effective =
            isSecret && next === "" && clearedRef.current.has(name)
              ? null
              : next;
          onChange({ ...value, [name]: effective });
        };
        const isRequired = (schema.required ?? []).includes(name);
        const savedMarker = savedSecret ? (
          <span className="ml-2 inline-flex items-center gap-1 align-middle text-[10px] font-semibold text-stone-400">
            {t("Saved")}
            {!isRequired && (
              <button
                type="button"
                className="rounded bg-stone-200 px-1 py-0.5 text-stone-600 hover:bg-stone-300 dark:bg-white/10 dark:text-stone-300 dark:hover:bg-white/20"
                disabled={disabled}
                onClick={() => set(null)}
              >
                {t("Clear saved")}
              </button>
            )}
          </span>
        ) : null;
        if (property.type === "boolean")
          return (
            <label
              key={name}
              className="flex items-center gap-2 self-end rounded-lg border border-stone-200 px-3 py-2.5 text-sm dark:border-white/10"
            >
              <Toggle
                disabled={disabled}
                checked={Boolean(current)}
                onChange={(event) => set(event.target.checked)}
              />
              {label}
              {savedMarker}
            </label>
          );
        return (
          <label key={name}>
            <span className="label">
              {label}
              {(schema.required ?? []).includes(name) ? " *" : ""}
              {savedMarker}
            </span>
            {property.enum ? (
              <select
                disabled={disabled}
                className="field"
                value={String(current)}
                onChange={(event) => set(event.target.value)}
              >
                <option value="">{t("Select…")}</option>
                {property.enum.map((option) => (
                  <option key={String(option)} value={String(option)}>
                    {String(option)}
                  </option>
                ))}
              </select>
            ) : property.widget === "textarea" ? (
              <textarea
                disabled={disabled}
                className="field"
                rows={3}
                placeholder={cleared ? t("Cleared") : undefined}
                value={String(current)}
                onChange={(event) => set(event.target.value)}
              />
            ) : (
              <input
                disabled={disabled}
                className="field"
                type={
                  property.secret
                    ? "password"
                    : ["number", "integer"].includes(property.type ?? "")
                      ? "number"
                      : "text"
                }
                step={property.type === "integer" ? 1 : undefined}
                placeholder={cleared ? t("Cleared") : undefined}
                value={String(current)}
                onChange={(event) =>
                  set(
                    ["number", "integer"].includes(property.type ?? "") &&
                      event.target.value !== ""
                      ? Number(event.target.value)
                      : event.target.value,
                  )
                }
              />
            )}{" "}
            {property.description && (
              <span className="mt-1 block text-[11px] text-stone-400">
                {property.description}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
}
